import { ReactNode, useState } from "react";
import t from "@/lang/lang";
import {
  updateAhuDataCache,
  useHtzAirHandlingUnitDataPutMutation,
} from "@/services/backend/htz/ahu/api/ahu-data-api";
import {
  AirHandlingUnitData,
  persistedShape,
} from "@/services/backend/htz/ahu/types/air-handling-unit-data";
import { AhuComponentCatalog } from "@/services/backend/htz/ahu/types/ahu-component-catalog";
import { AhuComponentsSchema } from "@/services/backend/htz/ahu/components/ahu-components-schema";
import { AhuComponentsTable } from "@/services/backend/htz/ahu/components/ahu-components-table";
import { AddComponentDropdown } from "@/services/backend/htz/ahu/components/add-component-dropdown";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/components/ui/tabs";
import { TextInputField } from "@/shared/components/field/input-field";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { inputValidationErrorMessages } from "@/shared/components/errors/input-error-messages";
import { useDebouncedMutation } from "@/shared/lib/debounce/debounce";
import { useAppDispatch } from "@/shared/hooks/redux-hooks";

/**
 * AhuDataEditCard edits one ahu data instance: its base data and its
 * components, as a schema or as a list. Changes are saved automatically.
 *
 * The card edits a local copy of the ahu data. The schema is a controlled
 * canvas which reports back every measurement it makes on its nodes.
 * Writing those into the RTK Query cache would hand the canvas a new node
 * array, which makes it measure again - an endless loop. Base data and
 * components are saved by the same put, so neither overwrites the other.
 */
export function AhuDataEditCard({
  ahuData,
  catalog,
  disabled,
  description,
}: {
  ahuData: AirHandlingUnitData;
  catalog: AhuComponentCatalog;
  disabled?: boolean;
  description?: ReactNode;
}) {
  const dispatch = useAppDispatch();
  const [tab, setTab] = useState<string>("schema");

  const [request, setRequest] = useState<AirHandlingUnitData>(ahuData);
  // Only changes to persisted data are saved, so the measurements the
  // schema reports while opening do not trigger a save.
  const [saveRequest, setSaveRequest] = useState<AirHandlingUnitData>(ahuData);

  const { error } = useDebouncedMutation(
    saveRequest,
    setSaveRequest,
    useHtzAirHandlingUnitDataPutMutation(),
    ahuData,
    {
      delay: 1000,
      resetOnError: false,
      // The cache is not patched while editing (see above), so the saved
      // state is written back once the save succeeded.
      onSuccess: (_result, saved) => {
        dispatch(updateAhuDataCache(saved));
      },
    },
  );

  const onAhuDataChange = (next: AirHandlingUnitData) => {
    setRequest(next);
    if (!disabled && persistedShape(next) !== persistedShape(request)) {
      setSaveRequest(next);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Anlagendaten")}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent className="grid gap-4">
        <div className="grid gap-2 md:grid-cols-4">
          <TextInputField
            label={t("Bezeichnung")}
            text={request.designation}
            onTextChange={(designation) =>
              onAhuDataChange({ ...request, designation })
            }
            errors={inputValidationErrorMessages(error, "designation")}
            disabled={disabled}
          />
          <TextInputField
            label={t("Standort")}
            text={request.location}
            onTextChange={(location) =>
              onAhuDataChange({ ...request, location })
            }
            errors={inputValidationErrorMessages(error, "location")}
            disabled={disabled}
          />
          <TextInputField
            label={t("Baujahr")}
            text={request.constructionYear}
            onTextChange={(constructionYear) =>
              onAhuDataChange({ ...request, constructionYear })
            }
            errors={inputValidationErrorMessages(error, "constructionYear")}
            disabled={disabled}
          />
          <TextInputField
            label={t("Luftstrom m³/h")}
            text={request.volumeFlowRate}
            onTextChange={(volumeFlowRate) =>
              onAhuDataChange({ ...request, volumeFlowRate })
            }
            errors={inputValidationErrorMessages(error, "volumeFlowRate")}
            disabled={disabled}
          />
        </div>
        <Tabs value={tab} onValueChange={setTab} className="flex flex-col">
          <div className="flex items-center justify-between">
            <TabsList>
              <TabsTrigger value="schema">{t("Schema")}</TabsTrigger>
              <TabsTrigger value="list">{t("Liste")}</TabsTrigger>
            </TabsList>
            {tab === "list" && !disabled && (
              <AddComponentDropdown
                ahuData={request}
                onAhuDataChange={onAhuDataChange}
                catalog={catalog}
              />
            )}
          </div>
          <TabsContent value="schema">
            <AhuComponentsSchema
              ahuData={request}
              onAhuDataChange={onAhuDataChange}
              ahuComponentCatalog={catalog}
              disabled={disabled}
            />
          </TabsContent>
          <TabsContent value="list">
            <AhuComponentsTable
              ahu={request}
              onAhuChange={onAhuDataChange}
              disabled={disabled}
            />
          </TabsContent>
        </Tabs>
        <RTKQueryErrorAlert error={error} ignoreValidationError={false} />
      </CardContent>
    </Card>
  );
}
